import React, { useState, useEffect } from 'react';
import { useAddin } from '../App';
import Modal from './Modal';
import * as webhook from '../services/webhook';

/**
 * TaskModal — Comprehensive modal for creating and editing tasks
 * Supports all Clio API features for tasks.
 */
export default function TaskModal({ isOpen, onClose, onSuccess, initialTask = null, matterId = null }) {
    const { linkedMatter, userEmail, userSettings } = useAddin();
    const actualMatterId = matterId || linkedMatter?.id;
    const taskTemplates = userSettings?.taskTemplates || [];

    // Default form state
    const defaultForm = {
        name: '',
        description: '',
        description_text_type: 'plain_text',
        assignee_id: '',
        assignee_type: 'User',
        priority: 'Normal',
        status: 'pending',
        due_at: '',
        notify_assignee: false,
        notify_completion: false,
        permission: 'public',
        time_estimated: '',
        statute_of_limitations: false,
        cascading: false,
        cascading_offset: '',
        cascading_offset_polarity: 'CalendarDays',
        cascading_offset_type: 'After',
        cascading_source: ''
    };

    const [form, setForm] = useState(defaultForm);
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(false);
    const [error, setError] = useState(null);

    // Dependent data options
    const [users, setUsers] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [tasks, setTasks] = useState([]);

    useEffect(() => {
        if (isOpen) {
            // Reset form or load initial data
            if (initialTask) {
                setForm({
                    ...defaultForm,
                    ...initialTask,
                    due_at: initialTask.due_at ? initialTask.due_at.substring(0, 10) : '',
                    assignee_id: initialTask.assignee?.id || '',
                    assignee_type: initialTask.assignee?.type || 'User',
                    time_estimated: initialTask.time_estimated || '',
                    cascading_offset: initialTask.cascading_offset || '',
                    cascading_source: initialTask.cascading_source?.id || initialTask.cascading_source || ''
                });
            } else {
                setForm(defaultForm);
            }

            setError(null);
            fetchDependentData();
        }
    }, [isOpen, initialTask]);

    const fetchDependentData = async () => {
        if (!actualMatterId) return;
        setFetchingData(true);
        try {
            // Fetch Users, Matter Contacts, and Tasks in parallel
            const [usersRes, matterRes, tasksRes] = await Promise.all([
                webhook.getUsers({ userEmail }).catch(() => []),
                webhook.getMatterDetails({ userEmail, matterId: actualMatterId }).catch(() => ({})),
                webhook.matterTasks({ userEmail, matterId: actualMatterId, taskAction: 'fetch' }).catch(() => [])
            ]);

            // Parse users
            let usersData = [];
            if (Array.isArray(usersRes)) usersData = usersRes;
            else if (usersRes?.data && Array.isArray(usersRes.data)) usersData = usersRes.data;
            else if (usersRes?.users && Array.isArray(usersRes.users)) usersData = usersRes.users;
            setUsers(usersData);

            // Parse contacts from matter details
            const matterInfo = matterRes?.matter || {};
            const contactsList = [];
            if (matterInfo.client) {
                contactsList.push({ id: matterInfo.client.id, name: matterInfo.client.name, role: 'Client' });
            }
            if (matterInfo.relationships && Array.isArray(matterInfo.relationships)) {
                matterInfo.relationships.forEach(rel => {
                    if (rel.contact && !contactsList.find(c => c.id === rel.contact.id)) {
                        contactsList.push({ id: rel.contact.id, name: rel.contact.name, role: rel.relationship_type });
                    }
                });
            }
            setContacts(contactsList);

            // Parse tasks for cascading
            let tasksData = [];
            if (Array.isArray(tasksRes)) tasksData = tasksRes;
            else if (tasksRes?.data && Array.isArray(tasksRes.data)) tasksData = tasksRes.data;
            else if (tasksRes?.data?.tasks && Array.isArray(tasksRes.data.tasks)) tasksData = tasksRes.data.tasks;
            else if (tasksRes?.tasks && Array.isArray(tasksRes.tasks)) tasksData = tasksRes.tasks;
            setTasks(tasksData);

        } catch (err) {
            console.error('[TaskModal] Failed to fetch dependent data:', err);
        } finally {
            setFetchingData(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleTemplateSelect = (e) => {
        const tplId = e.target.value;
        if (!tplId) return;

        const tpl = taskTemplates.find(t => t.id === tplId);
        if (tpl) {
            setForm(prev => ({
                ...prev,
                name: tpl.name || prev.name,
                description: tpl.description || prev.description,
                priority: tpl.priority || prev.priority,
                time_estimated: tpl.time_estimated || prev.time_estimated
            }));
        }
    };

    const handleSubmit = async () => {
        // Validation
        if (!form.name.trim()) {
            setError('Name is a required field.');
            return;
        }
        if (!form.description.trim()) {
            setError('Description is a required field.');
            return;
        }
        if (!form.assignee_id) {
            setError('Assignee is a required field.');
            return;
        }

        // Cascading validation
        if (form.cascading) {
            if (!form.cascading_source) {
                setError('Cascading Source (Parent Task) is required when cascading is enabled.');
                return;
            }
            if (!form.cascading_offset && form.cascading_offset !== 0) {
                setError('Cascading Offset is required when cascading is enabled.');
                return;
            }
        }

        setLoading(true);
        setError(null);

        try {
            // Build taskData payload
            const taskData = {
                name: form.name.trim(),
                description: form.description.trim(),
                description_text_type: form.description_text_type,
                priority: form.priority,
                status: form.status,
                permission: form.permission,
                notify_assignee: form.notify_assignee,
                notify_completion: form.notify_completion,
                statute_of_limitations: form.statute_of_limitations,
                assignee: {
                    id: parseInt(form.assignee_id, 10),
                    type: form.assignee_type
                }
            };

            // Options that can be null/omitted
            if (form.due_at) taskData.due_at = new Date(form.due_at).toISOString();
            if (form.time_estimated) taskData.time_estimated = parseInt(form.time_estimated, 10);

            if (form.cascading) {
                taskData.cascading = true;
                taskData.cascading_offset = parseInt(form.cascading_offset, 10);
                taskData.cascading_offset_polarity = form.cascading_offset_polarity;
                taskData.cascading_offset_type = form.cascading_offset_type;
                taskData.cascading_source = parseInt(form.cascading_source, 10);
            } else {
                taskData.cascading = false;
            }

            const isEdit = !!initialTask;
            const action = isEdit ? 'update' : 'create';

            await webhook.matterTasks({
                userEmail,
                matterId: actualMatterId,
                taskAction: action,
                taskId: isEdit ? initialTask.id : undefined,
                etag: isEdit ? initialTask.etag : undefined,
                taskData
            });

            onSuccess();
            onClose();
        } catch (err) {
            console.error('[TaskModal] Submit error:', err);
            setError(err.message || 'Failed to save task.');
        } finally {
            setLoading(false);
        }
    };

    const isUsersAssignee = form.assignee_type === 'User';

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={initialTask ? "Edit Task" : "Create Task"}>
            {fetchingData && <div style={{ fontSize: '13px', color: '#8a8a8a', marginBottom: '10px' }}>Loading options...</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '60vh', overflowY: 'auto', paddingRight: '8px' }}>

                {/* Template Selection */}
                {!initialTask && taskTemplates.length > 0 && (
                    <div className="form-group" style={{ background: '#f5f8fa', padding: '10px', borderRadius: '6px', border: '1px solid #e1e8ed' }}>
                        <label className="form-label" style={{ color: 'var(--navy)', fontWeight: 'bold' }}>Use a Template</label>
                        <select className="form-input" onChange={handleTemplateSelect} defaultValue="">
                            <option value="" disabled>Select a task template to auto-fill details...</option>
                            {taskTemplates.map(tpl => (
                                <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Basic Info */}
                <div className="section-label" style={{ marginTop: 0 }}>Basic Info</div>
                <div className="form-group">
                    <label className="form-label">Task Name <span style={{ color: 'var(--orange)' }}>*</span></label>
                    <input className="form-input" name="name" value={form.name} onChange={handleChange} placeholder="e.g. Review documents" />
                </div>

                <div className="form-group">
                    <label className="form-label">Description <span style={{ color: 'var(--orange)' }}>*</span></label>
                    <textarea className="form-input" name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Task description..." />
                </div>

                {/* Assignment & Status */}
                <div className="section-label">Assignment & Status</div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label className="form-label">Assignee Type</label>
                        <select className="form-input" name="assignee_type" value={form.assignee_type} onChange={handleChange}>
                            <option value="User">Internal User</option>
                            <option value="Contact">Matter Contact</option>
                        </select>
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label className="form-label">Assignee <span style={{ color: 'var(--orange)' }}>*</span></label>
                        <select className="form-input" name="assignee_id" value={form.assignee_id} onChange={handleChange}>
                            <option value="">Select Assignee...</option>
                            {isUsersAssignee
                                ? users.map(u => <option key={`u-${u.id}`} value={u.id}>{u.name || u.email || `User ${u.id}`}</option>)
                                : contacts.map(c => <option key={`c-${c.id}`} value={c.id}>{c.name} ({c.role})</option>)
                            }
                        </select>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label className="form-label">Status</label>
                        <select className="form-input" name="status" value={form.status} onChange={handleChange}>
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="in_review">In Review</option>
                            <option value="complete">Complete</option>
                        </select>
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label className="form-label">Priority</label>
                        <select className="form-input" name="priority" value={form.priority} onChange={handleChange}>
                            <option value="Low">Low</option>
                            <option value="Normal">Normal</option>
                            <option value="High">High</option>
                        </select>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label className="form-label">Due Date {!form.cascading && '(Optional)'}</label>
                        <input className="form-input" type="date" name="due_at" value={form.due_at} onChange={handleChange} disabled={form.cascading} />
                        {form.cascading && <div style={{ fontSize: '11px', color: '#8a8a8a', marginTop: '4px' }}>Calculated via cascading</div>}
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                        <label className="form-label">Estimated Time (mins)</label>
                        <input className="form-input" type="number" name="time_estimated" value={form.time_estimated} onChange={handleChange} min="0" placeholder="e.g. 60" />
                    </div>
                </div>

                {/* Configuration */}
                <div className="section-label">Configuration</div>
                <div className="form-group">
                    <label className="form-label">Permissions</label>
                    <select className="form-input" name="permission" value={form.permission} onChange={handleChange}>
                        <option value="public">Public</option>
                        <option value="private">Private</option>
                    </select>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                        <input type="checkbox" name="notify_assignee" checked={form.notify_assignee} onChange={handleChange} style={{ accentColor: 'var(--teal)' }} />
                        Notify Assignee on Creation
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                        <input type="checkbox" name="notify_completion" checked={form.notify_completion} onChange={handleChange} style={{ accentColor: 'var(--teal)' }} />
                        Notify Me on Completion
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                        <input type="checkbox" name="statute_of_limitations" checked={form.statute_of_limitations} onChange={handleChange} style={{ accentColor: 'var(--teal)' }} />
                        Mark as Statute of Limitations
                    </label>
                </div>

                {/* Cascading */}
                <div className="section-label">Cascading Options</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', marginBottom: '8px' }}>
                    <input type="checkbox" name="cascading" checked={form.cascading} onChange={handleChange} style={{ accentColor: 'var(--teal)' }} />
                    Enable Task Cascading
                </label>

                {form.cascading && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '10px', background: 'var(--selection-bg)', borderRadius: '4px' }}>
                        <div className="form-group">
                            <label className="form-label">Parent Task <span style={{ color: 'var(--orange)' }}>*</span></label>
                            <select className="form-input" name="cascading_source" value={form.cascading_source} onChange={handleChange}>
                                <option value="">Select Parent Task...</option>
                                {tasks.filter(t => t.id !== initialTask?.id).map(t => (
                                    <option key={`t-${t.id}`} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div className="form-group" style={{ flex: 1 }}>
                                <label className="form-label">Offset <span style={{ color: 'var(--orange)' }}>*</span></label>
                                <input className="form-input" type="number" name="cascading_offset" value={form.cascading_offset} onChange={handleChange} placeholder="e.g. 5" />
                            </div>
                            <div className="form-group" style={{ flex: 2 }}>
                                <label className="form-label">Offset Unit</label>
                                <select className="form-input" name="cascading_offset_polarity" value={form.cascading_offset_polarity} onChange={handleChange}>
                                    <option value="CalendarDays">Calendar Days</option>
                                    <option value="BusinessDays">Business Days</option>
                                    <option value="CalendarWeeks">Calendar Weeks</option>
                                    <option value="CalendarMonths">Calendar Months</option>
                                    <option value="CalendarYears">Calendar Years</option>
                                </select>
                            </div>
                            <div className="form-group" style={{ flex: 1 }}>
                                <label className="form-label">Type</label>
                                <select className="form-input" name="cascading_offset_type" value={form.cascading_offset_type} onChange={handleChange}>
                                    <option value="After">After</option>
                                    <option value="Before">Before</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <div className="modal-feedback error" style={{ marginTop: '12px' }}>
                    ⚠ {error}
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                <button
                    onClick={onClose}
                    style={{ background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-color)', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer' }}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    className="modal-submit teal"
                    onClick={handleSubmit}
                    disabled={loading}
                    style={{ width: 'auto', padding: '6px 20px', margin: 0 }}
                >
                    {loading && <div className="btn-spinner" />}
                    {loading ? 'Saving...' : (initialTask ? 'Save Changes' : 'Create Task')}
                </button>
            </div>
        </Modal>
    );
}
